"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { isLoadingBoundaries } from "../db/get-index";
import { filterQueryOptions } from "../lib/react-query";
import type { FilterSearchParams } from "../types/filter";
import type { JobMapPin } from "../types/job";
import { useClient } from "../utils/client/client";
import { normalizeFilterSearchParams } from "../utils/filterSearch/utils";
import { useFilterSearch } from "./use-filter-search";
import { useGeoSearch } from "./use-geosearch";
import { useLocale } from "./use-locale";
import { useSearchState } from "./use-search-state";
import { useSearchqueryState } from "./use-searchquery-state";
const EMPTY_LIST: readonly JobMapPin[] = [];

/**
 * The filter search only fetches PAGE_SIZE results.
 * If the map is open, we need all results to display the pins on the map.
 * This is why we have two hooks.
 * The query to get the pins is very minimal to reduce the data that needs to
 * be transferred over the network.
 *
 * If total hits < PAGE_SIZE, we can use the result of the filter search.
 */
export function useFilterSearchMapPins() {
  const $ = _c(26);
  const locale = useLocale();
  const client = useClient();
  const {
    getQueryState
  } = useSearchState();
  const [searchQuery] = useSearchqueryState();
  const {
    geoLocation,
    boundaries,
    radius
  } = useGeoSearch();
  const [isMounted, setIsMounted] = useState(false);
  const mainFilterResult = useFilterSearch();
  const [isMapVisible] = useLocalStorage("mapVisible", true);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setIsMounted(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const firstPage = mainFilterResult.data?.pages[0];
  const isFirstPageAlsoLastPage = typeof firstPage?.nextPage !== "number";
  let t2;
  if ($[2] !== boundaries || $[3] !== geoLocation || $[4] !== radius) {
    t2 = isLoadingBoundaries(geoLocation, radius, boundaries);
    $[2] = boundaries;
    $[3] = geoLocation;
    $[4] = radius;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isLoading = t2;
  const needsQuery = mainFilterResult.isSuccess && !isFirstPageAlsoLastPage;
  let t3;
  let t4;
  if ($[6] !== boundaries || $[7] !== client || $[8] !== geoLocation || $[9] !== getQueryState || $[10] !== isLoading || $[11] !== isMapVisible || $[12] !== isMounted || $[13] !== locale || $[14] !== needsQuery || $[15] !== radius || $[16] !== searchQuery) {
    const filter = getQueryState();
    const filterSearchParams = normalizeFilterSearchParams({
      searchQuery: searchQuery || "",
      filter,
      geoLocation,
      boundaries,
      radius,
      subclient: client
    });
    t3 = useQuery;
    t4 = filterQueryOptions.mapPins(locale, filterSearchParams, {
      enabled: isMounted && needsQuery && !isLoading && isMapVisible,
      refetch: true
    });
    $[6] = boundaries;
    $[7] = client;
    $[8] = geoLocation;
    $[9] = getQueryState;
    $[10] = isLoading;
    $[11] = isMapVisible;
    $[12] = isMounted;
    $[13] = locale;
    $[14] = needsQuery;
    $[15] = radius;
    $[16] = searchQuery;
    $[17] = t3;
    $[18] = t4;
  } else {
    t3 = $[17];
    t4 = $[18];
  }
  const results = t3(t4);
  firstPage?.jobs;
  let t5;
  bb0: {
    if (!isMounted) {
      t5 = undefined;
      break bb0;
    }
    let t6;
    if ($[19] !== firstPage?.jobs) {
      t6 = firstPage?.jobs.map(_temp);
      $[19] = firstPage?.jobs;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    t5 = t6;
  }
  const jobMapPinsFromFirstPage = t5;
  let t6;
  if ($[21] !== isMounted || $[22] !== results.data?.jobMapPins) {
    t6 = () => {
      if (!isMounted) {
        return;
      }
      return results.data?.jobMapPins;
    };
    $[21] = isMounted;
    $[22] = results.data?.jobMapPins;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  results.data?.jobMapPins;
  let t7;
  if ($[24] !== t6) {
    t7 = t6();
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const jobMapPinsFromQuery = t7;
  let t8;
  bb1: {
    if (!isMounted) {
      t8 = EMPTY_LIST;
      break bb1;
    }
    if (jobMapPinsFromFirstPage && !needsQuery) {
      t8 = jobMapPinsFromFirstPage;
      break bb1;
    }
    if (jobMapPinsFromQuery) {
      t8 = jobMapPinsFromQuery;
      break bb1;
    }
    t8 = EMPTY_LIST;
  }
  const jobMapPins = t8;
  return jobMapPins;
}
function _temp(job) {
  return {
    data: job.data,
    _geoloc: job._geoloc
  };
}